import { AbstractConnector } from '@web3-react/abstract-connector'
import { ConnectorUpdate } from '@web3-react/types'

import { getUzhLinkWalletProvider, hasUzhLinkAddress } from '../uzhLinkWallet/provider'

/** UZH Wallet (PWA) reached through #connect= / #pay= links; independent of window.ethereum. */
export class UzhLinkConnector extends AbstractConnector {
  constructor({ supportedChainIds }: { supportedChainIds?: number[] } = {}) {
    super({ supportedChainIds })
    this.handleAccountsChanged = this.handleAccountsChanged.bind(this)
    this.handleChainChanged = this.handleChainChanged.bind(this)
  }

  private handleAccountsChanged(accounts: unknown): void {
    const list = accounts as string[]
    if (!list?.length) this.emitDeactivate()
    else this.emitUpdate({ account: list[0] })
  }

  private handleChainChanged(chainId: unknown): void {
    this.emitUpdate({ chainId: chainId as string })
  }

  public async activate(): Promise<ConnectorUpdate> {
    const provider = getUzhLinkWalletProvider()
    provider.on('accountsChanged', this.handleAccountsChanged)
    provider.on('chainChanged', this.handleChainChanged)
    const accounts = (await provider.request({ method: 'eth_requestAccounts' })) as string[]
    const chainId = (await provider.request({ method: 'eth_chainId' })) as string
    return { provider, chainId, account: accounts[0] }
  }

  public async getProvider() {
    return getUzhLinkWalletProvider()
  }

  public async getChainId(): Promise<string> {
    return (await getUzhLinkWalletProvider().request({ method: 'eth_chainId' })) as string
  }

  public async getAccount(): Promise<null | string> {
    const accounts = (await getUzhLinkWalletProvider().request({ method: 'eth_accounts' })) as string[]
    return accounts[0] ?? null
  }

  public async isAuthorized(): Promise<boolean> {
    return hasUzhLinkAddress()
  }

  /** "Disconnect" in AccountDetails: forget the shared address, which ends the session. */
  public async close(): Promise<void> {
    await getUzhLinkWalletProvider().request({ method: 'wallet_revokePermissions' })
    this.emitDeactivate()
  }

  public deactivate(): void {
    const provider = getUzhLinkWalletProvider()
    provider.removeListener('accountsChanged', this.handleAccountsChanged)
    provider.removeListener('chainChanged', this.handleChainChanged)
  }
}
